/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-12-18 11:16:39
 */
import './index.scss'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Tree, Input, message, Tooltip } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  FolderAddOutlined
} from '@ant-design/icons'
import { v4 as uuid } from 'uuid'
import { produce } from 'immer'
import classnames from 'classnames'
import CONFIG from '../../../../constants/config'
import {
  useAppDispatch,
  useAppSelector,
  useIsAdmin
} from '../../../../app/hooks'
import {
  calcCharactersLen,
  debounce,
  raplaceEmoji as replaceEmoji
} from '../../../../utils/utils'
import {
  createFolder,
  cutFolders,
  cutPhotos,
  fetchFolderStatistic,
  fetchGroupFolders
} from '../../../../actions/folder'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import folderIcon from '../../../../images/photo_folder.png'
import { AntTreeNode } from 'antd/lib/tree'
import { useFolderCreateGate } from '../../FolderPaywall/useFolderCreateGate'
import { fetchFolderQuota } from '../../../../slices/folderQuotaSlice'

const { TreeNode } = Tree

/**
 * 要操作的资源，包含文件夹和照片
 */
interface ResourceProps {
  folders?: string[]
  photos?: string[]
}

interface FoldersPickerProps {
  visible: boolean
  title?: string
  showAIFolders?: boolean
  resource: ResourceProps
  onRefresh?: () => void
  action?: 'move' | undefined
  curFolderID?: string
  canSelectRoot?: boolean
  checkedNum: number
  onSubmit?: (params: any) => void
  onClose: () => void
  initialFolders?: string[]
  place: string
  source: number
  dataSource?: string
}

const FoldersPicker: React.FC<FoldersPickerProps> = ({
  visible,
  title = '移动到文件夹',
  action,
  showAIFolders = true,
  checkedNum,
  canSelectRoot,
  resource,
  onRefresh,
  curFolderID,
  onSubmit,
  onClose,
  initialFolders = [], // 暂时没有回显的需求
  place,
  source,
  dataSource
}) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const isVip = useAppSelector((state) => state.group.isVip)
  const isAdmin = useIsAdmin()

  const [editingId, setEditingId] = useState('')
  const [checkedFolders, setCheckedFolders] = useState<string[]>([])
  const [rootFolders, setRootFolders] = useState<any>([])
  const [subFolders, setSubFolders] = useState<any>({})
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [checkedFolderLevel, setCheckedFolderLevel] = useState<number>(0)
  // 文件夹数量全局卡点（普通 + AI 文件夹共用配额）· 弹窗内置，调用方无需关心
  const folderCreateGate = useFolderCreateGate()

  //当前选中的文件夹是否AI文件夹
  const isSelectedAIFolder = useMemo(() => {
    return checkedFolders.some((item) => {
      const folder = rootFolders.find(
        (folder) => String(folder.folderID) === item
      )
      return folder?.folderType > 0
    })
  }, [rootFolders, checkedFolders])

  const fetchFolders = (parentFolderID = 0) => {
    return dispatch(
      fetchGroupFolders({
        groupID,
        parentFolderID
      })
    ).then((data) => {
      const folderIDs = data.map((item) => item.folderID)
      return handleFetchFolderStatistic(folderIDs)
        .then((statisticList) => {
          // statisticList 已经是处理后的数组格式
          const statisticArray = Array.isArray(statisticList)
            ? statisticList
            : []

          // 将统计信息合并到文件夹对象中
          const foldersWithStatistic = data.map((folder) => {
            const statistic = statisticArray.find(
              (stat) => stat.folderID === folder.folderID
            )
            if (statistic) {
              return {
                ...folder,
                folderCount: statistic.folderCount || folder.folderCount || 0
              }
            }
            return folder
          })

          if (parentFolderID === 0) {
            setRootFolders(foldersWithStatistic)
          } else {
            setSubFolders({
              ...subFolders,
              [String(parentFolderID)]: foldersWithStatistic
            })
          }
        })
        .catch((err) => {
          console.error('获取文件夹统计数据失败:', err)
          // 即使统计接口失败，也设置文件夹列表
          if (parentFolderID === 0) {
            setRootFolders(data)
          } else {
            setSubFolders({
              ...subFolders,
              [String(parentFolderID)]: data
            })
          }
        })
    })
  }

  const handleFetchFolderStatistic = (folderIDs: string[]) => {
    return dispatch(
      fetchFolderStatistic({
        groupID,
        folderIDs
      })
    ).then((res) => {
      return res?.data || []
    })
  }

  useEffect(() => {
    fetchFolders()
  }, [])

  useEffect(() => {
    if (visible) {
      setCheckedFolders(initialFolders.map((item) => String(item)))
    } else {
      setEditingId('')
      setCheckedFolders([])
    }
  }, [visible])

  const handleCreateClick = () => {
    reportSaClickEvent('createNewFolder')

    // 数量卡点：超限拉开通弹窗并拦截（fail-open：配额未就绪/失败放行，后端兜底）
    if (folderCreateGate()) {
      const parentFolderID = Number(checkedFolders[0] || 0)
      const newFolder = {
        folderID: `new_${uuid()}`,
        folderName: '未命名文件夹',
        folderCount: 0,
        folderType: 0,
        parentFolderID
      }
      if (parentFolderID === 0) {
        setRootFolders([newFolder, ...(rootFolders || [])])
      } else {
        setSubFolders({
          ...subFolders,
          [String(parentFolderID)]: [
            newFolder,
            ...(subFolders[String(parentFolderID)] || [])
          ]
        })
        setExpandedKeys([...expandedKeys, String(parentFolderID)])
      }
      setEditingId(newFolder.folderID)
    }
  }

  const handleCreateFinish = (parentFolderID, folderID, folderName) => {
    if (parentFolderID === 0) {
      setRootFolders(
        produce(rootFolders, (draft) => {
          const index = draft.findIndex((item) => item.folderID === editingId)
          draft[index].folderID = folderID
          draft[index].folderName = folderName
        })
      )
    } else {
      setSubFolders(
        produce(subFolders, (draft) => {
          const folders = draft[parentFolderID] || []
          const index = folders.findIndex((item) => item.folderID === editingId)
          folders[index].folderID = folderID
          folders[index].folderName = folderName
        })
      )
    }

    setEditingId('')
  }

  const handleCreateCancel = (parentFolderID) => {
    if (parentFolderID === 0) {
      setRootFolders(rootFolders.filter((item) => item.folderID !== editingId))
    } else {
      setSubFolders({
        ...subFolders,
        [String(parentFolderID)]: subFolders[parentFolderID].filter(
          (item) => item.folderID !== editingId
        )
      })
    }
    setEditingId('')
  }

  const handleClose = () => {
    reportSaClickEvent('cancel')
    onClose()
  }

  const reportSaClickEvent = (clickItem) => {
    const params: any = {
      clickItem,
      place,
      role: sensorsRole(myRole),
      groupID
    }
    if (dataSource) {
      params.fromPlace =
        dataSource === 'legacy' ? 'watermarkContent' : 'preciseSearch'
    }
    sensors.track('web_click_pop_workgroup_add_to_folder', params)
  }

  const handleSubmit = () => {
    reportSaClickEvent('done')
    if (onSubmit) {
      onSubmit({
        folderIDs: checkedFolders.map((item) => Number(item)),
        submitCallback: onClose
      })
    } else if (action === 'move') {
      const promises: any = []
      if (resource.folders?.length) {
        promises.push(
          dispatch(
            cutFolders({
              groupID,
              folderIDs: resource.folders.map((item) => Number(item)),
              dstParentFolderID: Number(checkedFolders[0]),
              source
            })
          )
        )
      }
      if (resource.photos?.length) {
        promises.push(
          dispatch(
            cutPhotos({
              groupID,
              photoIDs: resource.photos,
              curFolderID: Number(curFolderID),
              dstFolderID: Number(checkedFolders[0]),
              source: 6
            })
          )
        )
      }
      Promise.all(promises).then(() => {
        message.success('操作成功')
        onRefresh && onRefresh()
        onClose()
      })
    }
  }

  const handleLoadData = (treeNode: AntTreeNode) => {
    const { folderID } = treeNode.props.dataRef

    return new Promise<void>((resolve) => {
      if (subFolders[folderID]?.length) {
        resolve()
        return
      }
      fetchFolders(Number(folderID)).then((data) => {
        resolve()
      })
    })
  }

  const renderTreeNodes = (data: any) => {
    return (
      data
        ?.filter((item) => (showAIFolders ? true : item.folderType === 0)) //只展示普通文件夹，不展示AI文件夹
        // ?.filter((item) => {
        //   if (action === 'move') {
        //     return !resource?.folders?.includes(item.folderID) // 移动时，不显示要操作的资源包含的文件夹自身及其子文件夹
        //   }
        //   return true
        // })
        ?.map((item: any) => {
          const isMoveSource =
            action === 'move' && resource?.folders?.includes(item.folderID) //移动文件夹时，禁止选择源文件夹自身
          const isCurFolder =
            action === 'move' && String(item.folderID) === String(curFolderID) //移动照片时，禁止选择当前所在文件夹
          const cutDisabled = isMoveSource || isCurFolder
          if (
            item.folderCount > 0 ||
            subFolders[String(item.folderID)]?.length
          ) {
            return (
              <TreeNode
                {...item}
                disabled={cutDisabled}
                disableCheckbox={cutDisabled}
                isLeaf={isMoveSource} // 只有源文件夹不可展开，当前文件夹仍可展开查看子文件夹
                key={item.folderID}
                title={
                  <FolderTreeNode
                    title={item.folderName}
                    disabled={cutDisabled}
                  />
                }
                dataRef={item}
              >
                {renderTreeNodes(subFolders[String(item.folderID)] || [])}
              </TreeNode>
            )
          }
          if (item.folderID === editingId) {
            return (
              <TreeNode
                {...item}
                key={item.folderID}
                title={
                  <NewFolderTreeNode
                    title={item.folderName}
                    parentFolderID={Number(item.parentFolderID)}
                    source={source}
                    onFinish={handleCreateFinish}
                    onCancel={handleCreateCancel}
                    reportSaClickEvent={reportSaClickEvent}
                  />
                }
                isLeaf
                dataRef={item}
                disableCheckbox
                selectable={false}
              />
            )
          }
          return (
            <TreeNode
              {...item}
              disabled={
                cutDisabled ||
                (item.folderType > 0 && !!resource?.folders?.length) //要操作的资源包含文件夹时，不可选中AI文件夹
              }
              key={item.folderID}
              isLeaf
              title={
                <FolderTreeNode
                  title={item.folderName}
                  disabled={cutDisabled}
                />
              }
              dataRef={item}
            />
          )
        })
    )
  }

  return (
    <Modal
      wrapClassName="add-to-folder-dialog"
      open={visible}
      title={
        <>
          <div>{title}</div>
          {checkedNum > 0 && (
            <div className="tip">
              已选中 {checkedNum} 张，点击「全选」滑到页面底部可选中全部照片
            </div>
          )}

          {myRole === CONFIG.memberRoleTypes.NORMAL && (
            <div className="tip warn">仅可操作自己拍摄的照片</div>
          )}
        </>
      }
      mask={{ closable: false }}
      onCancel={handleClose}
      width={650}
      footer={
        <div className="footer">
          {isAdmin && checkedFolderLevel < 10 && (
            <Button
              disabled={editingId !== '' || isSelectedAIFolder} // AI文件夹不可新建子文件夹
              icon={<FolderAddOutlined />}
              className="addBtn"
              onClick={handleCreateClick}
            >
              新建文件夹
            </Button>
          )}

          <div className="actions">
            <Button onClick={handleClose}>取消</Button>
            <Button
              type="primary"
              disabled={checkedFolders.length === 0}
              onClick={handleSubmit}
            >
              确定
            </Button>
          </div>
        </div>
      }
    >
      <div className="folders-dialog">
        <div
          className={classnames('root-folder-picker', {
            'root-folder-picker-checked': checkedFolders.includes('0')
          })}
        >
          <strong>分类文件夹</strong>
          {canSelectRoot && (
            <span
              onClick={() => {
                setCheckedFolders(['0'])
                setCheckedFolderLevel(0)
              }}
            >
              选择根目录
            </span>
          )}
        </div>

        <div className="folder-tree-wrapper">
          <Tree
            checkable
            checkStrictly
            loadData={handleLoadData as any}
            checkedKeys={checkedFolders.map((item) => String(item))}
            selectedKeys={[]}
            expandedKeys={expandedKeys}
            onExpand={(expandedKeys) =>
              setExpandedKeys(expandedKeys as string[])
            }
            onCheck={(checkedKeys: any, { checked, node }: any) => {
              const level = node.pos.split('-').length - 1
              setCheckedFolderLevel(level)
              setCheckedFolders([checkedKeys.checked.pop()])
            }}
            onSelect={(keys: any, { selected, node }: any) => {
              const level = node.pos.split('-').length - 1
              setCheckedFolderLevel(level)
              setCheckedFolders(keys as string[])
            }}
          >
            {renderTreeNodes(rootFolders)}
          </Tree>
        </div>
      </div>
    </Modal>
  )
}

const FolderTreeNode = ({ title, disabled }) => {
  if (disabled) {
    return (
      <Tooltip
        title="不支持移动文件夹到自己子文件夹下方"
        styles={{ root: { maxWidth: 260 } }}
      >
        <span style={{ width: 500 }}>
          <img src={folderIcon} alt="" />
          {title}
        </span>
      </Tooltip>
    )
  }
  return (
    <span style={{ width: 500 }}>
      <img src={folderIcon} alt="" />
      {title}
    </span>
  )
}

const folderNameOverLengthMessage = debounce(
  () => {
    message.warning('最多可输入16个汉字，32个字符!')
  },
  1000,
  true
)

const NewFolderTreeNode = ({
  title,
  parentFolderID = 0,
  source,
  onFinish,
  onCancel,
  reportSaClickEvent
}) => {
  const inputRef: any = useRef()
  const nodeRef: any = useRef()
  const [name, setName] = useState(title)
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)

  const handleValueChange = useCallback((e) => {
    const filterdValue = replaceEmoji(e.target.value.trim())
    if (calcCharactersLen(filterdValue, 32)) {
      setName(filterdValue)
    } else {
      folderNameOverLengthMessage()
    }
  }, [])

  const handleSave: any = useCallback(() => {
    reportSaClickEvent('confirmNewFolder')
    dispatch(
      createFolder({
        groupID,
        folderName: name,
        parentFolderID,
        source // 用于标识从哪个入口创建的文件夹，服务端上报埋点
      })
    ).then((data) => {
      if (data.status === 0) {
        onFinish(parentFolderID, data.folderID, name)
        // 刷新配额 used，让弹窗内连续新建时卡点判定不滞后
        if (groupID) dispatch(fetchFolderQuota({ groupID: String(groupID) }))
      } else if (data.status === -200) {
        message.error('操作失败，请重试')
      } else if (data.status === -204) {
        //TODO 超限
        message.error('操作失败，请重试 -204')
      }
    })
  }, [name, parentFolderID, source, onFinish])

  useEffect(() => {
    if (inputRef.current) {
      const inputElem: any = inputRef.current
      inputElem.focus()
      // AntD 6: 通过 nativeElement 或 input 获取原生 DOM
      const nativeInput = inputElem.nativeElement || inputElem.input
      if (nativeInput && nativeInput.value) {
        nativeInput.setSelectionRange(0, nativeInput.value.length)
      }
    }
  }, [inputRef])

  useEffect(() => {
    if (nodeRef.current) {
      // nodeRef.current.scrollIntoView()
    }
  }, [nodeRef])

  return (
    <span ref={nodeRef}>
      <img src={folderIcon} alt="" />
      <Input
        ref={inputRef}
        onPressEnter={handleSave}
        style={{ width: 200 }}
        maxLength={16}
        value={name}
        onChange={handleValueChange}
      />
      <CloseOutlined
        style={{ marginLeft: 10, color: 'red' }}
        onClick={() => {
          reportSaClickEvent('cancelNewFolder')
          onCancel(parentFolderID)
        }}
      />
      <CheckOutlined
        style={{ marginLeft: 10, color: 'green' }}
        onClick={handleSave}
      />
    </span>
  )
}

export default FoldersPicker
